import { defineStore } from 'pinia';
import ClientRowModel from '@/models/client/client-row';
import {
    EquipmentUsageInDepartmentSummaryViewModel,
    MiningMethodType,
    PlannedTaskGroupCutAndChainageViewModel,
    OfflineEditableCommand,
    ProductionFrontRulesViewModel,
    ShiftLocationDetailViewModel,
    TaskCommandResponse,
    UserNotificationSubject
} from '@/models/api';
import { useDepartmentStore } from './DepartmentStore';
import { useShiftDetails, useShiftEquipment } from '@/lib/stores/Shift';
import Equipment from '@/models/client/equipment';
import _, { cloneDeep, DebouncedFunc } from 'lodash';
import { ApplyConstraints, ClientOnlyTaskCommand } from '@/lib/services/Location';
import RequestHandler from '@/lib/api/RequestHandler';
import {
    GetClientOnlyCommandHandlers,
    GetCommandHandlers,
    GetCommandType,
    isTaskCommandResponse,
    NoOpChangeSetTransformerFactory
} from '@/lib/services/CommandFactory';
import ClientTaskModel from '@/models/client/client-task';
import { AddErrorToTask, AddWarningToTask, IsFloatDelay } from '@/lib/services/Task';
import {
    ClearAllTaskWarningsForGivenSources,
    clearTaskErrors,
    clearTaskNonLocationErrors,
    EQUIPMENT_ISSUE_TYPE,
    validateEquipmentUsage,
    validateTaskLocations
} from '@/components/ShiftBoard/Board/Validation';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useShiftWindowActuals } from '@/lib/stores/Production/ShiftWindowActualsStore';
import { RING_RULE_ISSUE_TYPE } from '@/components/ShiftBoard/Board/ValidationRingRules';
import { RING_STATE_ISSUE_TYPE } from '@/lib/services/Production/RingStateValidation';
import { TaskErrorType } from '@/models/client/types/task-error-type';
import { ProductionValidationTaskModel } from '@/models/client/production-validation-task-model';
import { BLAST_PACKET_STATE_ISSUE_TYPE } from '@/lib/services/Production/BlastPacketStateValidation';
import { ClientTaskWarning } from '@/models/client/client-task-warning';
import { ClientTaskIssueType } from '@/models/client/client-task-issues';
import { useSystemStore } from '@/lib/stores/SystemStore';
import { useSyncQueueStore } from '@/lib/stores/SyncQueueStore';
import { SyncTasksCommand } from '@/models/api';

export interface HeadingsWarning {
    subject: UserNotificationSubject
    text: string;
    targetTasks: ProductionValidationTaskModel[];
    affectedArea: AffectedAreaSpecification;
    source: ClientTaskIssueType;
}

export interface AffectedAreaSpecification {
    locationId: string,
    locationName: string,
    blastPacketId?: string | null,
    blastPacketName?: string | null,
    locationRingId?: string | null,
    locationRingName?: string | null,
}

export interface InterestingError {
    errorText: string,
    errorTargetTask: ClientTaskModel
}

const SYNC_DEBOUNCE_MS = 1000;
const REFRESH_HIGHLIGHT_MS = 1500;

interface PendingDebouncedSync {
    debouncedFn: DebouncedFunc<() => Promise<void>>;
    original: ClientRowModel;
    syncType: 'TASK' | 'ADHOC';
    departmentId: string;
    syncChangeSetTransformer: (changeSet: SyncTasksCommand) => SyncTasksCommand;
}

const pendingSyncs = new Map<string, PendingDebouncedSync>();

function cancelPendingSync(locationId: string) {
    const pending = pendingSyncs.get(locationId);
    if (pending) {
        pending.debouncedFn.cancel();
        pendingSyncs.delete(locationId);
    }
}

async function flushAllPendingSyncs(): Promise<void> {
    const cachedPendingSyncs = Array.from(pendingSyncs.values());
    await Promise.all(cachedPendingSyncs.map((pending) => pending.debouncedFn.flush()));
    pendingSyncs.clear();
}

function populateChainageAndCutNumberOnTask(task: ClientTaskModel, taskGroup: PlannedTaskGroupCutAndChainageViewModel) {
    if(task.taskType.allocateToNextCut){
        task.cutNumber = taskGroup.cutNumber + 1;
        task.startChainage = taskGroup.endChainage;
        task.endChainage = taskGroup.nextCutEndChainage;
    } else {
        task.cutNumber = taskGroup.cutNumber;
        task.startChainage = taskGroup.startChainage;
        task.endChainage = taskGroup.endChainage;
    }

}

export const useHeadingsStore = defineStore('headings', {
    state: () => {
        return {
            headings: [] as ClientRowModel[],
            rulesets: [] as ProductionFrontRulesViewModel[],
            warnings: [] as HeadingsWarning[],
            otherDepartmentEquipmentAvailability: [] as EquipmentUsageInDepartmentSummaryViewModel[],
            subscribedToFlushEvent: false,
            recentlyRefreshedLocationIds: [] as string[],
        };
    },
    getters: {
        tasks(): ClientTaskModel[] {
            return this.headings.flatMap((x) => x.tasks);
        },
        getHeadingById(): (headingId: string) => ClientRowModel {
            return (headingId: string) => {
                const match = this.headings.find((heading: ClientRowModel) => heading.id === headingId);
                if (match === undefined) throw new Error(`Could not find heading with Id ${headingId}`);

                return match;
            };
        },
        getHeadingByLocationId(): (locationId: string) => ClientRowModel {
            return (locationId: string) => {
                const match = this.headings.find((heading: ClientRowModel) => heading.location?.id === locationId);
                if (match === undefined) throw new Error(`Could not find heading with location Id ${locationId}`);

                return match;
            }
        },
        hasHeadingForLocation(): (locationId: string) => boolean {
            return (locationId: string) => this.headings.some((heading: ClientRowModel) => heading.location?.id === locationId);
        },
        isRecentlyRefreshed(state): (locationId: string) => boolean {
            return (locationId: string) => state.recentlyRefreshedLocationIds.includes(locationId);
        },
        hasPendingSyncForLocation(): (locationId: string) => boolean {
            return (locationId: string) => pendingSyncs.has(locationId) || (useSyncQueueStore().pendingCountsByLocation[locationId] ?? 0) > 0;
        },
        getTaskById(): (taskId: string) => ClientTaskModel {
            return (taskId: string) => {
                const match = this.tasks.find((task: ClientTaskModel) => task.id === taskId);
                if (match === undefined) throw new Error(`Could not find task with Id ${taskId}`);

                return match;
            };
        },
        getHeadingLocationDetails(): (locationId: string) => ShiftLocationDetailViewModel | null {
            return (locationId: string) => {
                const match = this.headings.find((x) => x.location?.id === locationId);
                return match?.shiftLocationDetails as ShiftLocationDetailViewModel;
            };
        },
        getHeadingByTask(state): (taskId: string) => ClientRowModel {
            return (taskId: string) => {
                const match = state.headings.find((x) => x.tasks.some((x) => x.id === taskId));
                if (match === undefined) throw new Error(`Could not find heading with task Id ${taskId}`);

                return match;
            };
        },
        getTasksWithEquipment(state): (id: string, equipmentId: string) => ClientTaskModel[] {
            return (id: string, equipmentId: string) => {
                const primaryEquipment = useShiftEquipment().getPrimaryEquipmentByEquipment(equipmentId);
                const tasks = this.tasks.filter(
                    (task: ClientTaskModel) =>
                        task.primaryEquipment !== null &&
                        (task.primaryEquipment.id === id ||
                            primaryEquipment.map((x) => x.id).includes(task.primaryEquipment.id))
                );

                tasks.sort((a, b) => {
                    if (a.startTime.isAfter(b.startTime)) return 1;
                    if (a.startTime.isBefore(b.startTime)) return -1;

                    return 0;
                });

                return tasks;
            };
        },
        getTasksWithStaff(state): (id: string) => ClientTaskModel[] {
            return (id: string) => {
                return this.tasks.filter((task: ClientTaskModel) => task.staffIds.includes(id)).sort((a, b) => {
                    if (a.startTime.isAfter(b.startTime)) return 1;
                    if (a.startTime.isBefore(b.startTime)) return -1;

                    return 0;
                });
            };
        },
        getTasksWithEquipmentByShift(state): (id: string, equipmentId: string) => ClientTaskModel[] {
            return (id: string, equipmentId: string) => {
                const shiftStartTime = useShiftDetails().shiftStartTime;
                const shiftEndTime = useShiftDetails().shiftEndTime;

                const tasks = this.getTasksWithEquipment(id, equipmentId).filter((task) => {
                    return task.endTime.isAfter(shiftStartTime) && task.startTime.isBefore(shiftEndTime);
                });

                return tasks;
            };
        },
    },
    actions: {
        setUpStore(headings: ClientRowModel[], rulesets: ProductionFrontRulesViewModel[], otherDepartmentEquipmentAvailability: EquipmentUsageInDepartmentSummaryViewModel[]) {
            this.headings = headings;
            this.rulesets = rulesets;
            this.otherDepartmentEquipmentAvailability = otherDepartmentEquipmentAvailability;
            this.validateAll();
        },
        updateOtherDepartmentEquipmentUsage(otherDepartmentEquipmentAvailability: EquipmentUsageInDepartmentSummaryViewModel[]) {
            this.otherDepartmentEquipmentAvailability = otherDepartmentEquipmentAvailability;
            this.validateAll();
        },
        addTaskGroupCutInformation(taskGroups: PlannedTaskGroupCutAndChainageViewModel[]) {
            for(const heading of this.headings){
                for(const task of heading.tasks){
                    const matchedTaskGroup = taskGroups.find(tg=>tg.plannedTaskGroupId == task.plannedCycleId);

                    if(matchedTaskGroup) {
                        populateChainageAndCutNumberOnTask(task, matchedTaskGroup);
                    }
                }
            }
        },
        addWarningForTask(subject: UserNotificationSubject, task: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) {
            this.warnings.push({
                text: warning.message,
                subject: subject,
                affectedArea: affectedArea,
                targetTasks: [task],
                source: warning.type
            });
        },
        updateStore(nextShiftHeadings: ClientRowModel[]) {
            // When we change shifts in offline mode, the headings have not had their tasks corrected based off of the previous shift's movements.
            // Unless we reassign tasks to THIS shifts headings the state will be inccorect.
            const headingsWithCorrectedTasks = nextShiftHeadings.map((x) => {
                return {
                    ...x,
                    tasks: this.tasks
                        .filter((task) => task.locationId === x.location!.id)
                        .sort((a, b) => {
                            if (a.startTime.isSame(b.startTime)) {
                                if (a.durationMinutes === 0 && b.durationMinutes !== 0) return -1;
                                else if (a.durationMinutes !== 0 && b.durationMinutes === 0) return 1;
                                else return 0;
                            }

                            return a.startTime.diff(b.startTime);
                        }),
                };
            });

            nextShiftHeadings.splice(0, nextShiftHeadings.length, ...headingsWithCorrectedTasks);
            this.headings = nextShiftHeadings;
            this.validateAll();
        },
        /**
         * Patch the availability (and idle/unavailable flag) carried on every task's copy of the given
         * planned equipment, then re-validate so equipment-usage warnings reflect the fresh state.
         */
        patchTasksForEquipment(equipment: Equipment) {
            this.applyEquipmentAvailabilityPatch([equipment]);
            this.validateAll();
        },
        /** As {@link patchTasksForEquipment} but for a batch of refreshed equipment. */
        patchTasksForEquipmentList(equipmentList: Equipment[]) {
            this.applyEquipmentAvailabilityPatch(equipmentList);
            this.validateAll();
        },
        applyEquipmentAvailabilityPatch(equipmentList: Equipment[]) {
            const byEquipmentId = new Map(equipmentList.map((e) => [e.equipmentId, e]));

            for (const heading of this.headings) {
                for (const task of heading.tasks ?? []) {
                    for (const plannedEquipment of task.plannedEquipment ?? []) {
                        if (plannedEquipment.equipmentId === null) continue;
                        const updated = byEquipmentId.get(plannedEquipment.equipmentId);
                        if (!updated) continue;

                        plannedEquipment.idleOrUnavailable = updated.idleOrUnavailable;
                        plannedEquipment.availability = updated.availability.map((a) => ({
                            _type: 'EquipmentAvailabilityViewModel',
                            id: a.id,
                            endsAt: a.endsAt,
                            equipmentAvailabilityType: a.type,
                            startsAt: a.startsAt,
                        }));
                    }
                }
            }
        },
        discardPendingSyncForLocation(locationId: string): boolean {
            const hadPending = this.hasPendingSyncForLocation(locationId);
            cancelPendingSync(locationId);
            useSyncQueueStore().clearLocation(locationId);
            return hadPending;
        },
        replaceHeadingForLocation(heading: ClientRowModel) {
            const index = this.headings.findIndex((row) => row.location?.id === heading.location?.id);
            if (index === -1) return;

            this.headings.splice(index, 1, heading);
            this.validateAllWithSpecificRowLocations(index);

            this.flagLocationRefreshed(heading.location!.id);
        },
        addHeading(heading: ClientRowModel) {
            const locationId = heading.location?.id;
            if (!locationId) return;

            // Dedupe: if this location is already displayed, refresh it in place instead of duplicating.
            if (this.headings.some((row) => row.location?.id === locationId)) {
                this.replaceHeadingForLocation(heading);
                return;
            }

            this.headings.push(heading);
            this.validateAll();

            this.flagLocationRefreshed(locationId);
        },
        removeHeadingsForLocations(locationIds: string[]) {
            const idSet = new Set(locationIds);

            const remaining = this.headings.filter((row) => !row.location || !idSet.has(row.location.id));
            if (remaining.length === this.headings.length) return;

            this.headings = remaining;
            this.warnings = this.warnings.filter((w) => !idSet.has(w.affectedArea.locationId ?? ''));

            this.validateAll();
        },
        flagLocationRefreshed(locationId: string) {
            if (!this.recentlyRefreshedLocationIds.includes(locationId)) {
                this.recentlyRefreshedLocationIds.push(locationId);
            }

            setTimeout(() => {
                this.recentlyRefreshedLocationIds = this.recentlyRefreshedLocationIds.filter((id) => id !== locationId);
            }, REFRESH_HIGHLIGHT_MS);
        },
        async executeTaskCommand(
            command: OfflineEditableCommand & { shiftId: string; departmentId: string; plannedLocationId: string },
            additionalClientOnlyCommands: ClientOnlyTaskCommand[] = []
        ) {
            const index = this.headings.findIndex((row) => row.id == command.plannedLocationId);
            const original = cloneDeep(this.headings[index]);

            if (index === -1) {
                throw new Error(`Could not find heading with id ${command.plannedLocationId} in shift store headings`);
            }

            const commandType = GetCommandType(command);
            const equipment = useShiftEquipment().equipment.map(
                (e) =>
                    ({
                        _type: 'PlannedEquipmentModel',
                        ...e,
                        equipmentRoleId: e.role?.id ?? null,
                        equipment: {
                            ...e,
                            equipmentRoleId: e.role?.id ?? null,
                            equipmentRole: { ...e.role },
                        },
                        equipmentAvailabilityType: e.type,
                    } as any)
            );

            const clientCommandHandlers = GetClientOnlyCommandHandlers(additionalClientOnlyCommands);
            const commandHandlers = GetCommandHandlers(commandType, this.headings[index].location!, useDepartmentStore().departmentId, equipment, useSystemStore().staffMembers, useDepartmentStore().taskTypes, useShiftWindowActuals().getBlastPacketTargetsDisplayInformation);
            // @ts-ignore
            const syncChangeSetTransformer = (commandHandlers.syncChangeSetTransformerFactory ?? NoOpChangeSetTransformerFactory)(command);

            // @ts-ignore
            commandHandlers.clientHandler(this.headings[index].tasks, command);

            for (const clientCommandHandler of clientCommandHandlers) {
                clientCommandHandler(this.headings[index].tasks);
            }

            if (commandHandlers.updateHeading)
                this.updateHeadingTasksAndValidate(command.plannedLocationId, this.headings[index].tasks);
            else this.validateAllWithSpecificRowLocations(index);

            const locationId = this.headings[index].location!.id;

            let response: any = null;

            if (commandHandlers.debounceable && (commandHandlers.syncType === 'TASK' || commandHandlers.syncType === 'ADHOC')) {
                const pending = pendingSyncs.get(locationId);
                if (pending) {
                    // Update the pending sync metadata but keep the original snapshot
                    pending.syncType = commandHandlers.syncType;
                    pending.departmentId = command.departmentId;
                    pending.syncChangeSetTransformer = syncChangeSetTransformer;
                    pending.debouncedFn();
                } else {
                    const headings = this.headings;
                    const newPending: PendingDebouncedSync = {
                        original,
                        syncType: commandHandlers.syncType,
                        departmentId: command.departmentId,
                        syncChangeSetTransformer: syncChangeSetTransformer,
                        debouncedFn: _.debounce(async () => {
                            const entry = pendingSyncs.get(locationId);
                            if (!entry) return;
                            pendingSyncs.delete(locationId);

                            const currentHeadingIndex = headings.findIndex((row) => row.location?.id === locationId);
                            if (currentHeadingIndex === -1) return;

                            const currentHeading = headings[currentHeadingIndex];
                            const currentTags = { ...currentHeading.tags };
                            currentHeading.tags.current = currentHeading.tags.next;
                            this.headings[index].tags.next = uuidv4();

                            if (entry.syncType === 'TASK') {
                                await RequestHandler.syncChangeSet(entry.original, currentHeading, currentTags, entry.syncChangeSetTransformer);
                            } else if (entry.syncType === 'ADHOC') {
                                // @ts-ignore
                                await RequestHandler.syncAdhocChangeSet(entry.original, currentHeading, entry.departmentId, currentTags);
                            }
                        }, SYNC_DEBOUNCE_MS),
                    };
                    pendingSyncs.set(locationId, newPending);
                    newPending.debouncedFn();
                }
            } else if (commandHandlers.syncType === 'TASK') {
                cancelPendingSync(locationId);
                const existingTags = { ... this.headings[index].tags };
                this.headings[index].tags.current = this.headings[index].tags.next;
                this.headings[index].tags.next = uuidv4();
                response = await RequestHandler.syncChangeSet(original, this.headings[index], existingTags, syncChangeSetTransformer);
            } else if (commandHandlers.syncType === 'ADHOC') {
                cancelPendingSync(locationId);
                const existingTags = { ... this.headings[index].tags };
                this.headings[index].tags.current = this.headings[index].tags.next;
                this.headings[index].tags.next = uuidv4();
                // @ts-ignore
                response = await RequestHandler.syncAdhocChangeSet(original, this.headings[index], command.departmentId, existingTags);
            } else {
                // syncType === 'NONE' — isolated API call, don't touch pending debounces
                // @ts-ignore
                response = await commandHandlers.apiHandler(command);
            }
        },
        actualsUpdatedForLocations(locationIds: string[]) {
              const headings = this.headings.filter(row=>locationIds.includes(row.location?.id ?? ''));

            const ringErrorAccumulator = (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) => {
                this.addWarningForTask(UserNotificationSubject.RingRules, t, affectedArea, warning);
                AddWarningToTask(t, warning);
            }

              for(const heading of headings) {
                  ClearAllTaskWarningsForGivenSources([RING_STATE_ISSUE_TYPE, BLAST_PACKET_STATE_ISSUE_TYPE], heading);
              }
              this.reEvaluateRingStatesForAllHeadings(ringErrorAccumulator);

              this.validateAll();
        },
        updateHeadingTasksAndValidate(headingId: string, newTasks: ClientTaskModel[], applyConstraints: boolean = true) {
            newTasks.sort((a, b) => {
                if (a.startTime.isSame(b.startTime)) {
                    if (a.durationMinutes === 0 && b.durationMinutes !== 0) return -1;
                    else if (a.durationMinutes !== 0 && b.durationMinutes === 0) return 1;
                    else return 0;
                }

                return a.startTime.diff(b.startTime);
            });
            const index = this.headings.findIndex((row) => row.id == headingId);

            if (index === -1) {
                throw new Error(`Could not find heading id ${headingId} in shift store headings`);
            }
            const heading = this.headings[index];

            const correctedTasks = applyConstraints ? ApplyConstraints(
                newTasks,
                // @ts-ignore
                heading.noGoZoneFinder,
                useDepartmentStore().taskTypes,
                heading.eosTask
            ): newTasks;

            const shiftEndTime = useShiftDetails().shiftEndTime;
            const firstTaskInNextShift = newTasks.find(
                (x) => x.startTime.isSameOrAfter(shiftEndTime) && IsFloatDelay(x) === false
            );
            const newEos = firstTaskInNextShift?.taskType.nameToDo ?? '';

            // Need to use Vue set here to keep reactivity for the store headings
            this.headings[index].tasks.splice(0, this.headings[index].tasks.length, ...correctedTasks);
            this.headings[index].eoS = newEos;

            const ringErrorAccumulator = (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) => {
                this.addWarningForTask(UserNotificationSubject.RingRules, t, affectedArea, warning);
                AddWarningToTask(t, warning);
            }

            ClearAllTaskWarningsForGivenSources([RING_STATE_ISSUE_TYPE, BLAST_PACKET_STATE_ISSUE_TYPE], this.headings[index]);
            this.clearWarningSummariesForSubjectsAndLocation([UserNotificationSubject.RingRules], [RING_STATE_ISSUE_TYPE, BLAST_PACKET_STATE_ISSUE_TYPE], this.headings[index].location!.id);
            this.reEvaluateRingStatesForHeading(this.headings[index], ringErrorAccumulator);
            this.validateAllWithSpecificRowLocations(index);
        },
        assignEquipment(rowId: string, taskId: string, equipmentId: string | null) {
            const task = this.getTaskById(taskId);
            task.equipmentAssignedOn = dayjs().utc();
            const index = this.headings.findIndex((row) => row.id == rowId);
            if (equipmentId == null) {
                task.plannedEquipment.splice(0, task.plannedEquipment.length);
                task.primaryEquipment = null;
                this.validateAllWithSpecificRowLocations(index);
                return;
            }
            const plannedEquipment = useShiftEquipment().getPlannedEquipment(equipmentId);

            task.primaryEquipment = {
                id: plannedEquipment.id,
                equipmentId: plannedEquipment.equipmentId!,
                name: plannedEquipment.name!,
                imageUrl: plannedEquipment.imageUrl!,
            };
            if (task.plannedEquipment.findIndex((x) => x.equipmentId === equipmentId) === -1) {
                task.plannedEquipment.splice(0, task.plannedEquipment.length);
                task.plannedEquipment.push({
                    ...plannedEquipment,
                    _type: 'ShiftPlannedEquipment',
                    equipmentRole: {
                        _type: 'EquipmentRoleViewModel',
                        id: plannedEquipment.role.id,
                        name: plannedEquipment.role.name,
                        isCrewType: plannedEquipment.role.isCrewType,
                        displayOrder: plannedEquipment.role.displayOrder,
                    },
                    equipmentRoleId: plannedEquipment.role.id,
                    equipmentId: plannedEquipment.equipmentId,
                    id: plannedEquipment.id,
                    imageUrl: plannedEquipment.imageUrl,
                    availability: plannedEquipment.availability.map((a) => ({
                        _type: 'EquipmentAvailabilityViewModel',
                        id: a.id,
                        endsAt: a.endsAt,
                        equipmentAvailabilityType: a.type,
                        startsAt: a.startsAt,
                    })),
                    idleOrUnavailable: plannedEquipment.idleOrUnavailable,
                    name: plannedEquipment.name,
                    showConflicts: plannedEquipment.showConflicts,
                    isPrimary: true,
                });
            }

            this.validateAllWithSpecificRowLocations(index);
        },
        validateAll() {
            this.clearWarningSummariesForSubjects([UserNotificationSubject.RingRules], [RING_RULE_ISSUE_TYPE, RING_STATE_ISSUE_TYPE, BLAST_PACKET_STATE_ISSUE_TYPE]);
            this.clearWarningSummariesForSubjects([UserNotificationSubject.Equipment], [EQUIPMENT_ISSUE_TYPE]);

            const equipmentErrorAccumulator = (t: ClientTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning, errorType: TaskErrorType) => {
                this.addWarningForTask(UserNotificationSubject.Equipment, t, affectedArea, warning);
                AddErrorToTask(t,warning.message,errorType, EQUIPMENT_ISSUE_TYPE);
            };

            const ringErrorAccumulator = (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) => {
                this.addWarningForTask(UserNotificationSubject.RingRules, t, affectedArea, warning);
                AddWarningToTask(t, warning);
            }

            for(const heading of this.headings) {
                clearTaskErrors(heading);
                ClearAllTaskWarningsForGivenSources([RING_RULE_ISSUE_TYPE, RING_STATE_ISSUE_TYPE], heading);
                validateTaskLocations(heading);
            }
            this.reEvaluateRingStatesForAllHeadings(ringErrorAccumulator);
            validateEquipmentUsage(this.headings, this.otherDepartmentEquipmentAvailability, equipmentErrorAccumulator);
            useShiftWindowActuals().validateProductionFrontRules(this.headings, this.rulesets, ringErrorAccumulator);
        },
        validateAllWithSpecificRowLocations(rowToValidateLocations: number) {
            this.clearWarningSummariesForSubjects([UserNotificationSubject.RingRules], [RING_RULE_ISSUE_TYPE]);
            this.clearWarningSummariesForSubjects([UserNotificationSubject.Equipment], [EQUIPMENT_ISSUE_TYPE]);

            const equipmentErrorAccumulator = (t: ClientTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning, errorType: TaskErrorType) => {
                this.addWarningForTask(UserNotificationSubject.Equipment, t, affectedArea, warning);
                AddErrorToTask(t,warning.message,errorType, EQUIPMENT_ISSUE_TYPE);
            };

            this.headings.filter((t, i) => i !== rowToValidateLocations).forEach(clearTaskNonLocationErrors); // Clear all errors except location-related ones for all headings
            this.headings.forEach(_.curry(ClearAllTaskWarningsForGivenSources)([RING_RULE_ISSUE_TYPE]));
            clearTaskErrors(this.headings[rowToValidateLocations]);
            validateTaskLocations(this.headings[rowToValidateLocations]);
            validateEquipmentUsage(this.headings, this.otherDepartmentEquipmentAvailability, equipmentErrorAccumulator);



            const accumulator = (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) => {
                this.addWarningForTask(UserNotificationSubject.RingRules, t, affectedArea, warning);
                AddWarningToTask(t, warning);
            }

            useShiftWindowActuals().validateProductionFrontRules(this.headings, this.rulesets, accumulator);
        },
        clearWarningSummariesForSubjects(subjects: UserNotificationSubject[], sourcesToRemove: ClientTaskIssueType[]) {
            this.warnings = this.warnings.filter(w=>!subjects.includes(w.subject) || !sourcesToRemove.includes(w.source));
        },
        clearWarningSummariesForSubjectsAndLocation(subjects: UserNotificationSubject[], sourcesToRemove: ClientTaskIssueType[], locationId: string) {
            this.warnings = this.warnings.filter(w=>w.affectedArea.locationId !== locationId || !subjects.includes(w.subject) || !sourcesToRemove.includes(w.source));
        },
        reEvaluateRingStatesForHeading(heading: ClientRowModel, ringErrorAccumulator: (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning)=>void){
            // get any associated locations due to sharing a stope
            if(heading.stopeInfo != null && heading.stopeInfo.supportsRings){
                const associatedHeadings = this.headings.filter(h=>h.stopeInfo?.stopeId === heading.stopeInfo!.stopeId)
                    .map(h=>({
                        ...h,
                        stopeInfo: h.stopeInfo!
                    }));
                useShiftWindowActuals().reEvaluateRingStatesForHeadings(associatedHeadings, ringErrorAccumulator);
            }
        },
        reEvaluateRingStatesForAllHeadings(ringErrorAccumulator: (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning)=>void){
            const headingsGroupedByStope = _.groupBy(this.headings.filter(h=>h.stopeInfo?.supportsRings == true).map(h=>({
                ...h,
                stopeInfo: h.stopeInfo!
            })), h=>h.stopeInfo!.stopeId);

            _.keys(headingsGroupedByStope).map(k=>{
                useShiftWindowActuals().reEvaluateRingStatesForHeadings(headingsGroupedByStope[k], ringErrorAccumulator);
            });
        },
        async flushPendingSyncs(): Promise<void> {
            if(pendingSyncs.size > 0){
                // try wait for all the pending syncs... if not possible... just wait a second. Let's not go crazy if the internet is garbage
                await Promise.race([flushAllPendingSyncs(), new Promise((resolve)=>setTimeout(resolve, 1000))]);
            }
        }
    },
});
